import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { isCuratedExerciseById } from '@cf-shared/data-model/exercise';

// Minimal structural shapes for the chain-walk — keeps this module free of the
// Angular service classes (and their transitive lodash-es imports) so the helper
// can be unit-tested in isolation. The full UserDb / ExerciseDbService satisfy
// these interfaces structurally, so call sites pass them as-is.
export interface LineageUdeLookup {
  getCustomExerciseByIdAsync(id: string): Promise<{ virtuallyDeleted?: boolean; local?: { sourceExerciseId?: string } }>;
}
export interface LineageGlobalLookup {
  getGlobalExerciseByIdOrThrowAsync(id: string, watcher: unknown): Promise<unknown | undefined>;
}

// Walk up the lineage chain from startId to the nearest live ancestor. Returns
// the ancestor id, or undefined when the chain is broken — cloud-falsy global
// (slug no longer in global_common), PouchNotFound for a hard-deleted UDE
// intermediate (pre-PRD data), cycle, or chain ends with no live ancestor.
// getCustomExerciseByIdAsync THROWS isPouchNotFound on missing — never returns
// falsy — so the catch block is the only "missing UDE" path.
// A read that FAILED is NOT a broken chain, and does not arrive here as `undefined`:
// getGlobalExerciseByIdOrThrowAsync throws every failure but a not-found, and this function
// does not catch it either. Answered as a broken chain, a read that timed out for a moment
// let the workout loader rotate a deleted fork's sub-slot onto the slot's default instead of
// back to its original (df-c21e11979ef2). A caller that must keep going catches it.
// Used by Revert (edit-exercise-page) and fork-Delete (exercise-switch).
export async function findNearestLiveAncestorAsync(
  startId: string | undefined,
  userDb: LineageUdeLookup,
  exerciseDb: LineageGlobalLookup,
  watcher: unknown,
): Promise<string | undefined> {
  const visited = new Set<string>();
  let cursorId: string | undefined = startId;
  while (cursorId) {
    if (visited.has(cursorId)) return undefined;
    visited.add(cursorId);
    if (isCuratedExerciseById(cursorId)) {
      const ex = await exerciseDb.getGlobalExerciseByIdOrThrowAsync(cursorId, watcher);
      return ex ? cursorId : undefined;
    }
    try {
      const ex = await userDb.getCustomExerciseByIdAsync(cursorId);
      if (!ex.virtuallyDeleted) return cursorId;
      cursorId = ex.local?.sourceExerciseId;
    } catch (err) {
      if (!isPouchNotFound(err)) throw err;
      return undefined;
    }
  }
  return undefined;
}
